import { ChevronDown, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { bqpRootUnit, businessOptionsForCompany, companyOptionsForState, isBqpBusiness, unitBelongsToCompany } from "../lib/company";
import { useOrgStore } from "../state/orgStore";
import type { CompanyFilter, Employee, LineStyle, OrgState, OrgUnit } from "../types/org";
import { GradeIcon } from "./GradeIcon";

interface UnitDraft {
  parentId: string;
  buBg: string;
  code: string;
  nameZh: string;
  nameEn: string;
  mGrade: string;
  managerEmployeeNo: string;
  isConcurrent: boolean;
  secretaryNameZh: string;
  lineStyle: LineStyle;
}

export function RightPanel({ readOnly = false }: { readOnly?: boolean }) {
  const state = useOrgStore((store) => store.state);
  const selectUnit = useOrgStore((store) => store.selectUnit);
  const selectEmployee = useOrgStore((store) => store.selectEmployee);
  const updateUnitWithParent = useOrgStore((store) => store.updateUnitWithParent);
  const updateEmployee = useOrgStore((store) => store.updateEmployee);
  const moveEmployee = useOrgStore((store) => store.moveEmployee);
  const addUnit = useOrgStore((store) => store.addUnit);
  const addEmployee = useOrgStore((store) => store.addEmployee);
  const saveEmployee = useOrgStore((store) => store.saveEmployee);
  const deleteUnit = useOrgStore((store) => store.deleteUnit);
  const deleteEmployee = useOrgStore((store) => store.deleteEmployee);
  const selectedUnit = state.selectedUnitId ? state.units[state.selectedUnitId] : undefined;
  const selectedEmployee = state.selectedEmployeeId ? state.employees[state.selectedEmployeeId] : undefined;
  const [deleteMessage, setDeleteMessage] = useState("");
  const [isEditingEmployee, setIsEditingEmployee] = useState(false);
  const [isEditingUnit, setIsEditingUnit] = useState(false);
  const [openEmployeeMenu, setOpenEmployeeMenu] = useState<"company" | "business" | undefined>();
  const [unitDraft, setUnitDraft] = useState<UnitDraft>(() => unitToDraft(selectedUnit));
  const managers = useMemo(
    () => Object.values(state.employees).sort((a, b) => displayEmployeeName(a).localeCompare(displayEmployeeName(b))),
    [state.employees],
  );
  const parentOptions = useMemo(() => selectedUnit ? parentUnitOptions(state, selectedUnit) : [], [selectedUnit, state]);
  const unitOptions = useMemo(() => Object.values(state.units).filter((unit) => !unit.isUnlinked).sort(compareUnit), [state.units]);
  const businessOptions = useMemo(() => selectedUnit ? businessUnitOptions(state, selectedUnit) : [], [selectedUnit, state]);
  const employeeCompanyOptions = useMemo(() => companyOptionsForState(state), [state]);
  const selectedEmployeeCompany = useMemo(
    () => selectedEmployee ? companyForEmployeeForm(selectedEmployee, employeeCompanyOptions, state.companyFilter) : state.companyFilter ?? employeeCompanyOptions[0] ?? "Qisda",
    [employeeCompanyOptions, selectedEmployee, state.companyFilter],
  );
  const employeeBusinessOptions = useMemo(() => businessOptionsForCompany(state, selectedEmployeeCompany), [selectedEmployeeCompany, state]);

  useEffect(() => {
    setIsEditingUnit(!readOnly && Boolean(selectedUnit?.isDraft));
    setDeleteMessage("");
    setUnitDraft(unitToDraft(selectedUnit));
  }, [readOnly, selectedUnit?.id, selectedUnit?.isDraft]);

  useEffect(() => {
    setIsEditingEmployee(!readOnly && Boolean(selectedEmployee?.isDraft));
    setDeleteMessage("");
    setOpenEmployeeMenu(undefined);
  }, [readOnly, selectedEmployee?.id, selectedEmployee?.isDraft]);

  if (!selectedUnit && !selectedEmployee) return null;

  const selectedEmployeeUnitId = selectedEmployee?.unitId ?? selectedEmployee?.draftUnitId ?? "";

  function updateEmployeeUnit(unitId?: string) {
    if (!selectedEmployee) return;
    const unit = unitId ? state.units[unitId] : undefined;
    if (selectedEmployee.isDraft) {
      updateEmployee(selectedEmployee.id, {
        draftUnitId: unit?.id,
        departmentCode: unit?.code,
        departmentNameZh: unit?.nameZh,
        departmentNameEn: unit?.nameEn,
        managerEmployeeNo: unit?.managerEmployeeNo,
        managerName: unit?.managerNameZh,
        buBg: unit?.buBg,
      });
      return;
    }
    if (unitId) moveEmployee(selectedEmployee.id, unitId);
    else updateEmployee(selectedEmployee.id, { unitId: undefined, departmentCode: undefined, departmentNameZh: undefined, departmentNameEn: undefined });
  }

  function commitEmployee() {
    if (!selectedEmployee) return;
    const saved = saveEmployee(selectedEmployee.id, selectedEmployeeUnitId);
    setDeleteMessage(saved ? "" : "請先選擇所屬單位");
    if (saved) setIsEditingEmployee(false);
  }

  if (selectedEmployee) {
    const selectedEmployeeUnit = selectedEmployeeUnitId ? state.units[selectedEmployeeUnitId] : undefined;
    const selectedEmployeeManager = managers.find((manager) => manager.employeeNo === selectedEmployee.managerEmployeeNo);
    return (
      <aside className="right-panel is-open">
        <div className="panel-title">
          <h2>人員屬性</h2>
          <div className="panel-title-actions">
            {!readOnly && !isEditingEmployee ? <button className="panel-action" onClick={() => setIsEditingEmployee(true)}>編輯</button> : null}
            <button className="icon-button" onClick={() => selectEmployee(undefined)} aria-label="關閉"><X size={16} /></button>
          </div>
        </div>
        {deleteMessage ? <div className="inline-warning">{deleteMessage}</div> : null}
        <div className="profile-strip">
          <span className={`avatar large person-avatar ${employeeAvatarCompanyClass(selectedEmployeeCompany)}`} aria-label="人員圖示" />
          <div><strong>{selectedEmployee.chineseName}</strong><small>{selectedEmployee.employeeNo}</small></div>
        </div>
        {!isEditingEmployee ? (
          <>
            <InfoRow label="員工編號" value={selectedEmployee.employeeNo} />
            <InfoRow label="中文姓名" value={selectedEmployee.chineseName} />
            <InfoRow label="英文姓名" value={formatOptional(selectedEmployee.englishName)} />
            <InfoRow label="公司別" value={formatOptional(selectedEmployee.companyCode)} />
            <InfoRow label="事業群/單位" value={formatOptional(selectedEmployee.buBg)} />
            <InfoRow label="所屬單位" value={selectedEmployeeUnit ? formatUnitOption(selectedEmployeeUnit) : formatOptional(selectedEmployee.departmentNameZh)} />
            <InfoRow label="主管" value={selectedEmployeeManager ? displayEmployeeName(selectedEmployeeManager) : formatOptional(selectedEmployee.managerName)} />
            <InfoRow label="P Grade" value={formatOptional(selectedEmployee.pGrade)} />
            <InfoRow label="職類名稱" value={formatOptional(selectedEmployee.jobFamily)} />
            <InfoRow label="職務名稱" value={formatOptional(selectedEmployee.jobTitle)} />
          </>
        ) : (
          <>
            <label>員工編號<input value={selectedEmployee.employeeNo} onChange={(event) => updateEmployee(selectedEmployee.id, { employeeNo: event.target.value })} /></label>
            <label>中文姓名<input value={selectedEmployee.chineseName} onChange={(event) => updateEmployee(selectedEmployee.id, { chineseName: event.target.value })} /></label>
            <label>英文姓名<input value={selectedEmployee.englishName ?? ""} onChange={(event) => updateEmployee(selectedEmployee.id, { englishName: event.target.value })} /></label>
            <PanelDropdown
              label="公司別"
              open={openEmployeeMenu === "company"}
              value={selectedEmployeeCompany}
              options={employeeCompanyOptions.map((company) => ({ label: company, value: company }))}
              onOpenChange={(open) => setOpenEmployeeMenu(open ? "company" : undefined)}
              onSelect={(value) => {
                const nextCompany = value as CompanyFilter;
                const nextBusinessOptions = businessOptionsForCompany(state, nextCompany);
                updateEmployee(selectedEmployee.id, {
                  companyCode: nextCompany,
                  buBg: nextBusinessOptions.includes(selectedEmployee.buBg ?? "") ? selectedEmployee.buBg : "",
                });
                setOpenEmployeeMenu(undefined);
              }}
            />
            <PanelDropdown
              label="事業群/單位"
              open={openEmployeeMenu === "business"}
              value={selectedEmployee.buBg ?? ""}
              options={[{ label: "未設定", value: "" }, ...employeeBusinessOptions.map((business) => ({ label: business, value: business }))]}
              onOpenChange={(open) => setOpenEmployeeMenu(open ? "business" : undefined)}
              onSelect={(value) => {
                updateEmployee(selectedEmployee.id, { buBg: value });
                setOpenEmployeeMenu(undefined);
              }}
            />
            <UnitCombobox
              label="所屬單位"
              units={unitOptions}
              selectedUnitId={selectedEmployeeUnitId}
              allowEmpty
              emptyLabel="不指派"
              onSelect={updateEmployeeUnit}
            />
            <ManagerCombobox
              label="主管"
              managers={managers}
              selectedEmployeeNo={selectedEmployee.managerEmployeeNo}
              onSelect={(manager) => updateEmployee(selectedEmployee.id, { managerEmployeeNo: manager?.employeeNo })}
            />
            <label>P Grade<input value={selectedEmployee.pGrade ?? ""} onChange={(event) => updateEmployee(selectedEmployee.id, { pGrade: event.target.value })} /></label>
            <label>職類名稱<input value={selectedEmployee.jobFamily ?? ""} onChange={(event) => updateEmployee(selectedEmployee.id, { jobFamily: event.target.value })} /></label>
            <label>職務名稱<input value={selectedEmployee.jobTitle ?? ""} onChange={(event) => updateEmployee(selectedEmployee.id, { jobTitle: event.target.value })} /></label>
            <div className="panel-edit-actions">
              <button className="primary-action" onClick={commitEmployee}>{selectedEmployee.isDraft ? "儲存員工" : "完成"}</button>
              <button className="secondary-action" onClick={() => setIsEditingEmployee(false)}>取消</button>
            </div>
            <button className="danger-action" onClick={() => window.confirm("確定要刪除此員工？") && deleteEmployee(selectedEmployee.id)}>刪除員工</button>
          </>
        )}
      </aside>
    );
  }

  const selectedManager = managers.find((employee) => employee.employeeNo === selectedUnit!.managerEmployeeNo);
  const draftManager = managers.find((employee) => employee.employeeNo === unitDraft.managerEmployeeNo);

  function updateUnitDraft(patch: Partial<UnitDraft>) {
    setUnitDraft((current) => ({ ...current, ...patch }));
  }

  function startUnitEdit() {
    setDeleteMessage("");
    setUnitDraft(unitToDraft(selectedUnit));
    setIsEditingUnit(true);
  }

  function cancelUnitEdit() {
    setDeleteMessage("");
    setUnitDraft(unitToDraft(selectedUnit));
    setIsEditingUnit(false);
  }

  function saveUnitEdit() {
    if (!selectedUnit) return;
    const manager = managers.find((employee) => employee.employeeNo === unitDraft.managerEmployeeNo);
    const saved = updateUnitWithParent(selectedUnit.id, unitDraft.parentId || undefined, {
      buBg: unitDraft.buBg,
      code: unitDraft.code,
      nameZh: unitDraft.nameZh,
      nameEn: unitDraft.nameEn,
      mGrade: unitDraft.mGrade,
      managerEmployeeNo: manager?.employeeNo,
      managerNameZh: manager?.chineseName,
      managerNameEn: manager?.englishName,
      isConcurrent: unitDraft.isConcurrent,
      secretaryNameZh: unitDraft.secretaryNameZh,
      lineStyle: unitDraft.lineStyle,
      isDraft: false,
    });
    if (!saved) {
      setDeleteMessage("無法儲存，請確認父組織不會形成循環，且父子單位屬於同一公司。");
      return;
    }
    setDeleteMessage("");
    setIsEditingUnit(false);
  }

  function editSelectedManager() {
    if (!selectedManager) return;
    selectEmployee(selectedManager.id);
  }

  return (
    <aside className="right-panel is-open">
      <div className="panel-title">
        <h2>單位屬性</h2>
        <div className="panel-title-actions">
          {!readOnly && !isEditingUnit ? <button className="panel-action" onClick={startUnitEdit}>編輯</button> : null}
          <button className="icon-button" onClick={() => selectUnit(undefined)} aria-label="關閉"><X size={16} /></button>
        </div>
      </div>
      {deleteMessage ? <div className="inline-warning">{deleteMessage}</div> : null}
      {!isEditingUnit ? (
        <>
          <InfoRow label="父組織" value={parentName(state, selectedUnit!)} />
          <InfoRow label="事業群/單位" value={selectedUnit!.buBg || "未設定"} />
          <InfoRow label="單位代碼" value={selectedUnit!.code} />
          <InfoRow label="單位名稱" value={selectedUnit!.nameZh} />
          <InfoRow label="M Grade" value={selectedUnit!.mGrade || "-"} />
          <InfoRow label="主管" value={selectedManager ? displayEmployeeName(selectedManager) : selectedUnit!.managerNameZh || "未設定主管"} />
          <InfoRow label="兼任設定" value={selectedUnit!.isConcurrent ? "是" : "否"} />
          <InfoRow label="秘書/助理" value={selectedUnit!.secretaryNameZh || "-"} />
          <InfoRow label="連線樣式" value={selectedUnit!.lineStyle === "solid" ? "正式匯報線" : "虛線支援/協作"} />
          {!readOnly ? <button className="secondary-action" onClick={editSelectedManager} disabled={!selectedManager}>編輯主管資訊</button> : null}
          {!readOnly ? <div className="unit-create-actions">
            <button className="primary-action" onClick={() => addUnit(selectedUnit!.id)}>新增子組織</button>
            <button className="primary-action" onClick={() => addEmployee(selectedUnit!.id)}>新增員工</button>
          </div> : null}
        </>
      ) : (
        <>
          <UnitCombobox
            label="父組織"
            units={parentOptions}
            selectedUnitId={unitDraft.parentId}
            allowEmpty
            emptyLabel="最上層組織"
            onSelect={(parentId) => updateUnitDraft({ parentId: parentId ?? "" })}
          />
          <label>事業群/單位<select value={unitDraft.buBg} onChange={(event) => updateUnitDraft({ buBg: event.target.value })}>
            <option value="">未設定</option>
            {businessOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select></label>
          <label>單位代碼<input value={unitDraft.code} onChange={(event) => updateUnitDraft({ code: event.target.value })} /></label>
          <label>單位名稱<input value={unitDraft.nameZh} onChange={(event) => updateUnitDraft({ nameZh: event.target.value })} /></label>
          <label>M Grade<input value={unitDraft.mGrade} onChange={(event) => updateUnitDraft({ mGrade: event.target.value })} /></label>
          <ManagerCombobox
            label="主管"
            managers={managers}
            selectedEmployeeNo={draftManager?.employeeNo}
            onSelect={(manager) => updateUnitDraft({ managerEmployeeNo: manager?.employeeNo ?? "" })}
          />
          <label>兼任設定<select value={unitDraft.isConcurrent ? "yes" : "no"} onChange={(event) => updateUnitDraft({ isConcurrent: event.target.value === "yes" })}>
            <option value="no">否</option>
            <option value="yes">是</option>
          </select></label>
          <label>秘書/助理<input value={unitDraft.secretaryNameZh} onChange={(event) => updateUnitDraft({ secretaryNameZh: event.target.value })} /></label>
          <label>連線樣式<select value={unitDraft.lineStyle} onChange={(event) => updateUnitDraft({ lineStyle: event.target.value as LineStyle })}>
            <option value="solid">正式匯報線</option>
            <option value="dashed">虛線支援/協作</option>
          </select></label>
          <div className="panel-edit-actions">
            <button className="primary-action" onClick={saveUnitEdit}>儲存</button>
            <button className="secondary-action" onClick={cancelUnitEdit}>取消</button>
          </div>
          <button className="danger-action" onClick={() => {
            const deleted = deleteUnit(selectedUnit!.id);
            setDeleteMessage(deleted ? "" : "此單位仍有子組織或人員，無法刪除。");
          }}>刪除單位</button>
        </>
      )}
    </aside>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PanelDropdown({
  label,
  open,
  value,
  options,
  onOpenChange,
  onSelect,
}: {
  label: string;
  open: boolean;
  value: string;
  options: Array<{ label: string; value: string }>;
  onOpenChange: (open: boolean) => void;
  onSelect: (value: string) => void;
}) {
  const selected = options.find((option) => option.value === value) ?? options[0];
  return (
    <label className="panel-dropdown custom-field">
      {label}
      <div className="custom-select">
        <button type="button" className="custom-select-trigger" onClick={() => onOpenChange(!open)} aria-expanded={open}>
          <b>{selected?.label ?? value}</b>
          <ChevronDown size={15} />
        </button>
        {open ? (
          <div className="custom-select-menu">
            {options.map((option) => (
              <button
                key={`${label}-${option.value || "empty"}`}
                type="button"
                className={option.value === value ? "is-selected" : ""}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onSelect(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </label>
  );
}

function ManagerCombobox({
  label,
  managers,
  selectedEmployeeNo,
  onSelect,
}: {
  label: string;
  managers: Employee[];
  selectedEmployeeNo?: string;
  onSelect: (manager?: Employee) => void;
}) {
  const selectedManager = managers.find((employee) => employee.employeeNo === selectedEmployeeNo);
  const [query, setQuery] = useState(() => selectedManager ? displayEmployeeName(selectedManager) : "");
  const [open, setOpen] = useState(false);
  const normalizedQuery = query.trim().toLowerCase();
  useEffect(() => {
    setQuery(selectedManager ? displayEmployeeName(selectedManager) : "");
  }, [selectedEmployeeNo, selectedManager]);
  const filteredManagers = useMemo(() => {
    if (!normalizedQuery) return managers.slice(0, 80);
    return managers.filter((manager) => managerSearchText(manager).includes(normalizedQuery)).slice(0, 80);
  }, [managers, normalizedQuery]);

  function selectManager(manager?: Employee) {
    onSelect(manager);
    setQuery(manager ? displayEmployeeName(manager) : "");
    setOpen(false);
  }

  return (
    <label className="manager-combobox">
      {label}
      <div className="manager-search">
        <Search size={15} />
        <input
          value={query}
          placeholder="搜尋中文名或英文名"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        />
      </div>
      {open ? (
        <div className="manager-options">
          <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectManager(undefined)}>未設定主管</button>
          {filteredManagers.length ? filteredManagers.map((manager) => (
            <button
              type="button"
              key={manager.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectManager(manager)}
            >
              <span>{displayEmployeeName(manager)}</span>
              <small>{manager.employeeNo}</small>
            </button>
          )) : <div className="manager-empty">查無符合的人員</div>}
        </div>
      ) : null}
    </label>
  );
}

function UnitCombobox({
  label,
  units,
  selectedUnitId,
  allowEmpty,
  emptyLabel = "未指派",
  onSelect,
}: {
  label: string;
  units: OrgUnit[];
  selectedUnitId?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  onSelect: (unitId?: string) => void;
}) {
  const selectedUnit = units.find((unit) => unit.id === selectedUnitId);
  const [query, setQuery] = useState(() => selectedUnit ? formatUnitOption(selectedUnit) : "");
  const [open, setOpen] = useState(false);
  const normalizedQuery = query.trim().toLowerCase();

  useEffect(() => {
    setQuery(selectedUnit ? formatUnitOption(selectedUnit) : "");
  }, [selectedUnitId, selectedUnit]);

  const filteredUnits = useMemo(() => {
    if (!normalizedQuery) return units.slice(0, 120);
    return units.filter((unit) => unitSearchText(unit).includes(normalizedQuery)).slice(0, 120);
  }, [normalizedQuery, units]);

  function selectUnitOption(unit?: OrgUnit) {
    onSelect(unit?.id);
    setQuery(unit ? formatUnitOption(unit) : "");
    setOpen(false);
  }

  return (
    <label className="unit-combobox manager-combobox">
      {label}
      <div className="manager-search">
        <Search size={15} />
        <input
          value={query}
          placeholder="搜尋單位代碼或名稱"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        />
      </div>
      {open ? (
        <div className="manager-options">
          {allowEmpty ? <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectUnitOption(undefined)}>{emptyLabel}</button> : null}
          {filteredUnits.length ? filteredUnits.map((unit) => (
            <button
              type="button"
              key={unit.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectUnitOption(unit)}
            >
              <span>{formatUnitOption(unit)}</span>
              <small>{unit.mGrade || `L${unit.level}`}</small>
            </button>
          )) : <div className="manager-empty">找不到符合的單位</div>}
        </div>
      ) : null}
    </label>
  );
}

function unitToDraft(unit?: OrgUnit): UnitDraft {
  return {
    parentId: unit?.parentId ?? "",
    buBg: unit?.buBg ?? "",
    code: unit?.code ?? "",
    nameZh: unit?.nameZh ?? "",
    nameEn: unit?.nameEn ?? "",
    mGrade: unit?.mGrade ?? "",
    managerEmployeeNo: unit?.managerEmployeeNo ?? "",
    isConcurrent: Boolean(unit?.isConcurrent),
    secretaryNameZh: unit?.secretaryNameZh ?? "",
    lineStyle: unit?.lineStyle ?? "solid",
  };
}

function parentName(state: OrgState, unit: OrgUnit) {
  if (unitBelongsToCompany(state, unit, "BQP")) {
    const root = bqpRootUnit(state);
    if (!root || unit.id === root.id) return "最上層組織";
  } else if (!unit.parentId) return "最上層組織";
  if (!unit.parentId) return "最上層組織";
  const parent = state.units[unit.parentId];
  return parent ? parent.nameZh || parent.nameEn || parent.code : "最上層組織";
}

function parentUnitOptions(state: OrgState, selectedUnit: OrgUnit) {
  const descendants = new Set<string>();
  addDescendants(selectedUnit.id, descendants, Object.values(state.units));
  const selectedSource = unitSourceSystem(state, selectedUnit);
  const selectedIsBqp = unitBelongsToCompany(state, selectedUnit, "BQP");
  return Object.values(state.units)
    .filter((unit) => {
      const source = unitSourceSystem(state, unit);
      if (selectedIsBqp && !unitBelongsToCompany(state, unit, "BQP")) return false;
      return !unit.isUnlinked && unit.id !== selectedUnit.id && (!selectedSource || !source || source === selectedSource) && !descendants.has(unit.id);
    })
    .sort(compareUnit);
}

function businessUnitOptions(state: OrgState, selectedUnit: OrgUnit) {
  const source = unitSourceSystem(state, selectedUnit);
  const values = new Set<string>();
  for (const unit of Object.values(state.units)) {
    if (source && unitSourceSystem(state, unit) !== source) continue;
    if (unit.buBg?.trim()) values.add(unit.buBg.trim());
  }
  for (const employee of Object.values(state.employees)) {
    if (employee.buBg?.trim()) values.add(employee.buBg.trim());
  }
  if (selectedUnit.buBg?.trim()) values.add(selectedUnit.buBg.trim());
  return [...values].sort((a, b) => a.localeCompare(b));
}

function addDescendants(unitId: string, descendants: Set<string>, units: OrgUnit[]) {
  for (const child of units.filter((unit) => unit.parentId === unitId)) {
    descendants.add(child.id);
    addDescendants(child.id, descendants, units);
  }
}

function compareUnit(a: OrgUnit, b: OrgUnit) {
  return compareCode(a.code, b.code) || a.level - b.level || a.nameZh.localeCompare(b.nameZh);
}

function compareCode(a: string, b: string) {
  const left = a.trim().toUpperCase();
  const right = b.trim().toUpperCase();
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right, "en", { numeric: true, sensitivity: "base" });
}

function formatUnitOption(unit: OrgUnit) {
  const name = unit.nameZh || unit.nameEn;
  return [unit.code, name].filter(Boolean).join(" ");
}

function unitSearchText(unit: OrgUnit) {
  return [unit.code, unit.nameZh, unit.nameEn, unit.buBg, unit.mGrade, unit.managerNameZh, unit.managerNameEn]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function unitSourceSystem(state: OrgState, unit?: OrgUnit) {
  let current = unit;
  while (current) {
    if (current.sourceSystem) return current.sourceSystem;
    current = current.parentId ? state.units[current.parentId] : undefined;
  }
  return undefined;
}

function displayEmployeeName(employee: Employee) {
  return [employee.chineseName, employee.englishName].filter(Boolean).join(" / ") || employee.employeeNo;
}

function managerSearchText(employee: Employee) {
  return [employee.chineseName, employee.englishName, employee.employeeNo].filter(Boolean).join(" ").toLowerCase();
}

function formatOptional(value?: string) {
  return value?.trim() ? value : "-";
}

function companyForEmployeeForm(employee: Employee, options: CompanyFilter[], fallback?: CompanyFilter): CompanyFilter {
  const exactCompany = options.find((company) => company === employee.companyCode);
  if (exactCompany) return exactCompany;
  const companyCode = String(employee.companyCode ?? "").toLowerCase();
  if ((companyCode.includes("bqp") || isBqpBusiness(employee.buBg)) && options.includes("BQP")) return "BQP";
  if (companyCode.includes("qisda") && options.includes("Qisda")) return "Qisda";
  if (companyCode.includes("benq") && options.includes("BenQ")) return "BenQ";
  if (fallback && options.includes(fallback)) return fallback;
  return options[0] ?? "Qisda";
}

function employeeAvatarCompanyClass(company: CompanyFilter) {
  return `is-${company.toLowerCase()}`;
}
